import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { Integration, IntegrationLog } from '../types';

const PROVIDERS = [
  {
    key: 'rdstation',
    label: 'RD Station',
    icon: 'ri-bar-chart-grouped-line',
    accent: '#00c4b4',
    description: 'Automação de marketing e CRM — sincronize leads e eventos do SaaS',
  },
];

const RD_EVENTS = [
  { value: 'subscription_created', label: 'Assinatura criada' },
  { value: 'subscription_cancelled', label: 'Assinatura cancelada' },
  { value: 'payment_received', label: 'Pagamento recebido' },
  { value: 'user_created', label: 'Novo usuário cadastrado' },
  { value: 'trial_started', label: 'Trial iniciado' },
  { value: 'trial_expired', label: 'Trial expirado' },
];

const STATUS_META = {
  connected: { label: 'Conectado', color: 'bg-emerald-500/20 text-emerald-400', icon: 'ri-checkbox-circle-line' },
  error:     { label: 'Erro',      color: 'bg-red-500/20 text-red-400',         icon: 'ri-error-warning-line' },
  untested:  { label: 'Não testado', color: 'bg-white/10 text-white/40',        icon: 'ri-question-line' },
};

interface Props {
  integrations: Integration[];
  onSave: (id: string, data: Partial<Integration>) => Promise<void>;
  onTest: (id: string, type: string) => Promise<{ ok: boolean; message: string }>;
  logs: IntegrationLog[];
}

export const MarketingIntegration = ({ integrations, onSave, onTest, logs }: Props) => {
  const [activeProvider, setActiveProvider] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [showToken, setShowToken] = useState(false);

  const current = integrations.find((i) => i.provider === activeProvider);
  const currentLogs = logs.filter((l) => l.integration_id === current?.id).slice(0, 8);

  useEffect(() => {
    if (current) setEditing({ ...current.config_json });
  }, [activeProvider, current?.id]);

  const handleSave = async () => {
    if (!current) return;
    setSaving(true);
    await onSave(current.id, { config_json: editing });
    setSaving(false);
  };

  const handleTest = async () => {
    if (!current) return;
    setTesting(true);
    setTestResult(null);
    const result = await onTest(current.id, 'test_connection');
    setTestResult(result);
    setTesting(false);
  };

  const toggleEvent = (event: string) => {
    const events = (editing.events as string[]) || [];
    const updated = events.includes(event) ? events.filter((e) => e !== event) : [...events, event];
    setEditing({ ...editing, events: updated });
  };

  const statusMeta = (status: string) => STATUS_META[status as keyof typeof STATUS_META] || STATUS_META.untested;

  return (
    <div className="space-y-6">
      {/* Provider cards */}
      <div className="grid grid-cols-3 gap-4">
        {PROVIDERS.map((p) => {
          const integ = integrations.find((i) => i.provider === p.key);
          const sm = statusMeta(integ?.last_status || 'untested');
          return (
            <button
              key={p.key}
              onClick={() => setActiveProvider(activeProvider === p.key ? null : p.key)}
              className={`text-left border rounded-2xl p-5 transition-all cursor-pointer ${
                activeProvider === p.key ? 'border-violet-500/50' : 'border-white/10 hover:border-white/20'
              }`}
              style={{ background: activeProvider === p.key ? `${p.accent}12` : `${p.accent}08` }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ background: `${p.accent}20` }}>
                  <i className={`${p.icon} text-[22px]`} style={{ color: p.accent }} />
                </div>
                <span className={`text-[11px] font-700 px-2.5 py-1 rounded-full flex items-center gap-1 ${sm.color}`}>
                  <i className={`${sm.icon} text-[12px]`} />
                  {sm.label}
                </span>
              </div>
              <h3 className="text-[15px] font-700 text-white mb-1">{p.label}</h3>
              <p className="text-[12px] text-white/40 leading-relaxed">{p.description}</p>
            </button>
          );
        })}

        {/* Coming soon cards */}
        {[
          { label: 'ActiveCampaign', icon: 'ri-mail-star-line', accent: '#356AE6' },
          { label: 'HubSpot', icon: 'ri-customer-service-2-line', accent: '#FF7A59' },
        ].map((p) => (
          <div key={p.label} className="border border-white/5 rounded-2xl p-5 opacity-50" style={{ background: `${p.accent}06` }}>
            <div className="w-12 h-12 flex items-center justify-center rounded-xl mb-4" style={{ background: `${p.accent}15` }}>
              <i className={`${p.icon} text-[22px]`} style={{ color: p.accent }} />
            </div>
            <h3 className="text-[15px] font-700 text-white mb-1">{p.label}</h3>
            <span className="text-[11px] text-white/30 font-600 px-2 py-0.5 bg-white/10 rounded-full">Em breve</span>
          </div>
        ))}
      </div>

      {/* Config panel */}
      {activeProvider && current && (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <i className="ri-bar-chart-grouped-line text-[18px] text-white/60" />
              <h2 className="text-[15px] font-700 text-white">Configurar RD Station</h2>
            </div>
            <button onClick={() => setActiveProvider(null)} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer">
              <i className="ri-close-line text-white/60" />
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-5">
                <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider">Credenciais</p>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Token da API</label>
                  <div className="relative">
                    <input
                      type={showToken ? 'text' : 'password'}
                      value={String(editing.token || '')}
                      onChange={(e) => setEditing({ ...editing, token: e.target.value })}
                      placeholder="Seu token RD Station Marketing"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 pr-10"
                    />
                    <button onClick={() => setShowToken(!showToken)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer" type="button">
                      <i className={`${showToken ? 'ri-eye-off-line' : 'ri-eye-line'} text-white/30 text-[14px]`} />
                    </button>
                  </div>
                  <p className="text-[10px] text-white/25 mt-1.5">Encontre em RD Station → Integrações → API</p>
                </div>

                <div>
                  <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Identificação da Conta</label>
                  <input
                    type="text"
                    value={String(editing.account_id || '')}
                    onChange={(e) => setEditing({ ...editing, account_id: e.target.value })}
                    placeholder="ID ou nome da conta"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20"
                  />
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div>
                      <p className="text-[13px] text-white font-600">Sincronizar leads</p>
                      <p className="text-[11px] text-white/30">Enviar novos cadastros para o RD Station</p>
                    </div>
                    <button
                      onClick={() => setEditing({ ...editing, sync_leads: !editing.sync_leads })}
                      className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative flex-shrink-0 overflow-hidden ${editing.sync_leads ? 'bg-violet-600' : 'bg-white/10'}`}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${editing.sync_leads ? 'left-6' : 'left-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div>
                      <p className="text-[13px] text-white font-600">Sincronizar eventos</p>
                      <p className="text-[11px] text-white/30">Enviar eventos financeiros para o RD Station</p>
                    </div>
                    <button
                      onClick={() => setEditing({ ...editing, sync_events: !editing.sync_events })}
                      className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative flex-shrink-0 overflow-hidden ${editing.sync_events ? 'bg-violet-600' : 'bg-white/10'}`}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${editing.sync_events ? 'left-6' : 'left-1'}`} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider mb-3">Eventos a Sincronizar</p>
                  <div className="space-y-2">
                    {RD_EVENTS.map((ev) => {
                      const events = (editing.events as string[]) || [];
                      const active = events.includes(ev.value);
                      return (
                        <button
                          key={ev.value}
                          onClick={() => toggleEvent(ev.value)}
                          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border transition-all cursor-pointer ${active ? 'bg-violet-600/10 border-violet-500/30' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                        >
                          <span className={`text-[13px] font-600 ${active ? 'text-white' : 'text-white/40'}`}>{ev.label}</span>
                          <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${active ? 'bg-violet-600' : 'bg-white/10'}`}>
                            {active && <i className="ri-check-line text-white text-[12px]" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Status */}
                <div className={`rounded-xl p-4 border ${current.last_status === 'connected' ? 'bg-emerald-500/10 border-emerald-500/20' : current.last_status === 'error' ? 'bg-red-500/10 border-red-500/20' : 'bg-white/5 border-white/10'}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <i className={`${statusMeta(current.last_status).icon} text-[15px] ${statusMeta(current.last_status).color.split(' ')[1]}`} />
                    <span className="text-[12px] font-700 text-white">Status da Conexão</span>
                  </div>
                  <p className="text-[11px] text-white/50">
                    {current.last_status === 'connected' && 'Conexão verificada com sucesso'}
                    {current.last_status === 'error' && (current.last_error || 'Falha na conexão')}
                    {current.last_status === 'untested' && 'Ainda não testado'}
                  </p>
                </div>

                {testResult && (
                  <div className={`rounded-xl p-3 flex items-start gap-2 ${testResult.ok ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                    <i className={`text-[14px] mt-0.5 ${testResult.ok ? 'ri-checkbox-circle-line text-emerald-400' : 'ri-error-warning-line text-red-400'}`} />
                    <p className="text-[12px] text-white/70">{testResult.message}</p>
                  </div>
                )}

                <button
                  onClick={handleTest}
                  disabled={testing}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/70 text-[13px] font-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${testing ? 'ri-loader-4-line animate-spin' : 'ri-wifi-line'} text-[14px]`} />
                  {testing ? 'Testando...' : 'Testar Conexão'}
                </button>

                {currentLogs.length > 0 && (
                  <div>
                    <p className="text-[11px] font-700 text-white/30 uppercase tracking-wider mb-2">Últimas Sincronizações</p>
                    <div className="space-y-1.5">
                      {currentLogs.map((log) => (
                        <div key={log.id} className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-2">
                          <div className="flex items-center gap-2">
                            <i className={`text-[12px] ${log.status === 'success' ? 'ri-checkbox-circle-line text-emerald-400' : 'ri-close-circle-line text-red-400'}`} />
                            <span className="text-[12px] text-white/60">{log.event_type}</span>
                          </div>
                          <span className="text-[11px] text-white/30">{new Date(log.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-white/10">
              <button onClick={() => { setActiveProvider(null); setTestResult(null); }} className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/60 text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {saving ? 'Salvando...' : 'Salvar Configurações'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
